// Signature-before-preview state helpers for MailLetterDialog. Pure + testable.
export const SIGNATURE_CONSENT_VERSION = "v1-2026-10-05";

export const SIG = {
  CHECKING: "checking",
  STATUS_ERROR: "status_error",
  NEEDS_AUTH: "needs_auth",
  SIGNING: "signing",
  SIGN_ERROR: "sign_error",
  SIGNED: "signed",
};

export const SIGNATURE_SEND_ERROR_CODES = [
  "missing_signature_authorization",
  "signature_name_mismatch",
  "signature_consent_invalid",
];

export function signatureBlockLines(name) {
  return [`/s/ ${name}`, name];
}

export function formatSignedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-US", {
    year: "numeric", month: "long", day: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
}

export function canApplySignature({ sigState, consentChecked, signatureName }) {
  return !!consentChecked && !!signatureName && (sigState === SIG.NEEDS_AUTH || sigState === SIG.SIGN_ERROR);
}

export function canProceedFromPreview({ identityMissing, sigState, previewState }) {
  return !identityMissing && sigState === SIG.SIGNED && previewState === "loaded";
}

export function isSubmitDisabled({ submitting, authorized, sigState, identityConfirmed, sufficient, priceChange }) {
  return !!submitting || !authorized || sigState !== SIG.SIGNED || !identityConfirmed || !sufficient || !!priceChange;
}

export function signErrorMessage(status, detail) {
  if (status === 422 && detail && typeof detail === "object") {
    if (detail.code === "missing_profile_name") {
      return detail.message || "Please add your legal first and last name to your profile before mailing.";
    }
    if (detail.code === "unsafe_signature_insertion") {
      return "We couldn't find a safe place to apply your signature on this letter. Please regenerate it from your report and try again.";
    }
  }
  if (typeof detail === "string" && detail) return detail;
  return detail?.message || "We couldn't record your signature authorization. Please try again.";
}

export function isSignedPreview(headers) {
  const v = headers?.["x-signature-applied"] ?? (typeof headers?.get === "function" ? headers.get("x-signature-applied") : "");
  return String(v || "").toLowerCase() === "true";
}

export async function applySignature(api, docId) {
  try {
    const r = await api.post(`/documents/${docId}/sign`, {});
    return { ok: true, info: r.data };
  } catch (e) {
    return { ok: false, message: signErrorMessage(e?.response?.status, e?.response?.data?.detail) };
  }
}

async function readBlobDetail(e) {
  try {
    const data = e?.response?.data;
    if (data && typeof data.text === "function") {
      const txt = await data.text();
      try { return JSON.parse(txt)?.detail || null; } catch { return txt || null; }
    }
    return data?.detail || null;
  } catch {
    return null;
  }
}

// Fetches a fresh packet preview and refuses anything the server did not sign.
export async function fetchSignedPreview(api, docId) {
  try {
    const r = await api.get(`/mailings/packet-preview/${docId}`, { responseType: "blob" });
    if (!isSignedPreview(r.headers)) {
      return { ok: false, kind: "unsigned", message: "Your signature authorization is no longer valid for this letter. Please authorize your signature again." };
    }
    return { ok: true, blob: r.data };
  } catch (e) {
    const detail = await readBlobDetail(e);
    const msg = typeof detail === "string" ? detail : detail?.message;
    if (e?.response?.status === 422) {
      return { ok: false, kind: "missing", message: msg || "Required identity documents are missing." };
    }
    return { ok: false, kind: "technical", message: msg || "We couldn't build the signed mailing packet right now." };
  }
}
